import React from 'react'
import moment from 'moment'
import { Grid, Segment } from 'semantic-ui-react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { listAppointments } from 'app-support/stats/state/requests'
import { DetailSegment } from './DetailSegment'
import { SmallSegment } from './SmallSegment'
import { YearSelector } from './YearSelector'
import { YearSwitcher } from './YearSwitcher'
import AppointmentsChartMonth from './Charts/Appointments/AppointmentsChartMonth'
import AppointmentsChartYear from './Charts/Appointments/AppointmentsChartYear'
import { apiFormat, format } from '@cbgms/base/utils/datetime'
import { endOfYear, endOfYearFn, firstYearFn, lastYearFn, startOfYear, startOfYearFn } from './../utils'
import { useNotifier } from '@cbgms/base/notifier'
import { Redirect } from 'react-router-dom'
import { IRootReducerState } from 'state/reducers'
import { useQuery } from '@tanstack/react-query'
import isEmpty from 'lodash/isEmpty'
import StatsLayout from './StatsLayout'

const Appointments: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()

  const [selectedYear, setSelectedYear] = React.useState(moment().year().toString())
  const formattedStartOfYear = startOfYearFn(selectedYear)
  const formattedEndOfYear = endOfYearFn(selectedYear)

  const [yearOffset, setYearOffset] = React.useState(0)
  const firstYear = firstYearFn(yearOffset)
  const lastYear = lastYearFn(yearOffset)
  const { country, shopModules, companyUUID, shopID } = useSelector((state: IRootReducerState) => state.stats)

  const appointmentsResult = useQuery(
    ['Statistics', 'Appointments', { selectedYear, yearOffset, country, shopModules, companyUUID, shopID }],
    async () => {
      const [monthResult, yearResult, graphResult, totalResult] = await Promise.all([
        dispatch(
          listAppointments({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: endOfYear,
            StartDate: startOfYear,
            TimeFrame: 'month',
            Country: country
          })
        ),
        dispatch(
          listAppointments({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: lastYear,
            StartDate: firstYear,
            TimeFrame: 'year',
            Country: country
          })
        ),
        dispatch(
          listAppointments({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: formattedEndOfYear,
            StartDate: formattedStartOfYear,
            TimeFrame: 'month',
            Country: country
          })
        ),
        dispatch(
          listAppointments({
            CompanyUUID: companyUUID,
            ShopID: shopID,
            ShopModules: shopModules,
            EndDate: endOfYear,
            StartDate: firstYear,
            TimeFrame: 'year',
            Country: country
          })
        )
      ])

      return {
        monthly: monthResult.Data,
        yearly: yearResult.Data,
        graph: graphResult.Data,
        total: totalResult.Data
      }
    },
    { refetchOnWindowFocus: false, refetchOnMount: false }
  )

  if (appointmentsResult.isFetched && isEmpty(appointmentsResult.data)) {
    notifier.error(t('global.something_went_wrong'))

    return <Redirect to='/stats' />
  }

  const totalAppointments = appointmentsResult.data?.total.reduce((totalValue, item) => {
    return totalValue + item.TotalAppointments
  }, 0)

  const totalMOT = appointmentsResult.data?.yearly.reduce((totalValue, item) => {
    return totalValue + item.TotalMOT
  }, 0)

  const totalTyreChange = appointmentsResult.data?.yearly.reduce((totalValue, item) => {
    return totalValue + item.TotalTyreChange
  }, 0)

  const currentMonthData = appointmentsResult.data?.monthly.find(
    item => item.StartDate === moment().startOf('month').format(apiFormat.yearMonthDay)
  )

  const lastMonthData = appointmentsResult.data?.monthly.find(
    item => item.StartDate === moment().startOf('month').subtract(1, 'month').format(apiFormat.yearMonthDay)
  )

  return (
    <StatsLayout testId='segment-appointments-stats' title={t('global.stats.appointments')} showDateFilters={false}>
      <Grid.Row columns='equal'>
        <Grid.Column>
          <DetailSegment
            title={t('global.stats.total', { entityName: t('global.stats.appointments') })}
            main_icon='calendar alternate outline'
            value={totalAppointments || 0}
          />
        </Grid.Column>
        <Grid.Column>
          <DetailSegment
            title={`${t('global.stats.current_month')}: ${moment().format(format.monthLong)}`}
            main_icon='calendar alternate outline'
            additional_icon='arrow down'
            value={(currentMonthData && currentMonthData.TotalAppointments) || 0}
          />
        </Grid.Column>
        <Grid.Column>
          <DetailSegment
            title={`${t('global.stats.last_month')}: ${moment().subtract(1, 'month').format(format.monthLong)}`}
            main_icon='calendar alternate outline'
            additional_icon='arrow left'
            value={(lastMonthData && lastMonthData.TotalAppointments) || 0}
          />
        </Grid.Column>
        <Grid.Column>
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <SmallSegment title={t('global.stats.mot')} icon='wrench' value={totalMOT || 0} />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <SmallSegment title={t('global.stats.tyre_change')} icon='dot circle' value={totalTyreChange || 0} />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row columns='equal'>
        <Grid.Column>
          <Segment className='segment-chart'>
            <YearSwitcher
              title={t('global.stats.per_year', { entityName: t('global.stats.appointments') })}
              setYear={setYearOffset}
              yearOffset={yearOffset}
              loading={appointmentsResult.isLoading}
            />
            <br />
            <br />
            <AppointmentsChartYear data={appointmentsResult.data?.yearly || []} />
          </Segment>
        </Grid.Column>
        <Grid.Column>
          <Segment className='segment-chart'>
            <YearSelector
              title={t('global.stats.per_month', { entityName: t('global.stats.appointments') })}
              placeholder={selectedYear.toString()}
              onYearChange={setSelectedYear}
              loading={appointmentsResult.isLoading}
            />
            <br />
            <AppointmentsChartMonth data={appointmentsResult.data?.graph || []} />
          </Segment>
        </Grid.Column>
      </Grid.Row>
    </StatsLayout>
  )
}

export default Appointments
